import { PlayArrow } from '@mui/icons-material';
import { Box, Drawer, Grid, IconButton, Theme, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ArchitectureDrawerProps } from '../../../@types/ContextProviderRelated';
import { ESubMenu, EWallMode } from '../../../@types/Enums';
import { IconAddDoor, IconAddPartitionWall, IconAddPassage, IconAddRadiator, IconAddWindow } from '../../../assets/icons';
import { useToolbarContext } from '../../../provider/ToolbarProvider';
import { immersightBlue } from '../../../utils/colors';
import ImsDoubleRectButton from '../ImsDoubleRectButton';

const ArchitectureDrawer = (props: ArchitectureDrawerProps): JSX.Element => {
  const { id, open, onClose } = props;
  const { setCurrentlyActiveSubMenu, wallMode, setWallMode } = useToolbarContext();

  useEffect(() => {
    if (!open) {
      setWallMode(EWallMode.NONE);
    }
    // reset wall mode when ArchitectureDrawer unmounts to handle indirect closures
    return () => {
      setWallMode(EWallMode.NONE);
      setCurrentlyActiveSubMenu(ESubMenu.NONE);
    };
  }, [open, setWallMode, setCurrentlyActiveSubMenu]);

  const handleCloseDrawer = (): void => {
    setWallMode(EWallMode.NONE);
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    onClose();
  };

  const handleItemClick = (buttonMode: EWallMode): void => {
    wallMode === buttonMode ? setWallMode(EWallMode.NONE) : setWallMode(buttonMode);
  };

  // Drawer container style
  const drawerSx: SystemStyleObject<Theme> = {
    '& .MuiPaper-root': {
      top: '64px',
      zIndex: 1000,
      width: '340px',
      height: 'calc(100% - 64px)',
    },
  };

  return (
    <Drawer key={id} anchor="right" open={open} variant={open ? 'permanent' : 'temporary'} sx={drawerSx}>
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="ArchitectureDrawer.header" />
            </Typography>
          </Box>

          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              color: '#fff',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>

        {/* Content area below the header */}
        <Grid container item direction="column" spacing={3} p={2}>
          {/* DOOR */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton icon={<IconAddDoor />} selected={wallMode === EWallMode.DOOR} onClick={() => handleItemClick(EWallMode.DOOR)} />
              </Grid>
              <Grid item>
                <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                  <FormattedMessage id="ArchitectureDrawer.door" />
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* PASSAGE */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton icon={<IconAddPassage />} selected={wallMode === EWallMode.PASSAGE} onClick={() => handleItemClick(EWallMode.PASSAGE)} />
              </Grid>
              <Grid item>
                <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                  <FormattedMessage id="ArchitectureDrawer.passage" defaultMessage="Add Passages" />
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* WINDOW */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton icon={<IconAddWindow />} selected={wallMode === EWallMode.WINDOW} onClick={() => handleItemClick(EWallMode.WINDOW)} />
              </Grid>
              <Grid item>
                <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                  <FormattedMessage id="ArchitectureDrawer.window" />
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* PARTITION WALL */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton icon={<IconAddPartitionWall />} selected={wallMode === EWallMode.PARTITION} onClick={() => handleItemClick(EWallMode.PARTITION)} />
              </Grid>
              <Grid item>
                <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                  <FormattedMessage id="ArchitectureDrawer.partitionWall" />
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* RADIATOR */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton icon={<IconAddRadiator />} selected={wallMode === EWallMode.RADIATOR} onClick={() => handleItemClick(EWallMode.RADIATOR)} />
              </Grid>
              <Grid item>
                <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                  <FormattedMessage id="ArchitectureDrawer.radiator" />
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default ArchitectureDrawer;
